import axios from 'axios'
import clsx from 'clsx'
import {debounce} from 'lodash'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {
  DndContext,
  closestCenter,
  TouchSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {TableSkeleton} from '../../../../../../_gori/helpers/components'
import {
  Button,
  DropdownButton,
  TableBody,
  TableTabs,
  TableWrapper,
} from '../../../../../../_gori/partials/widgets'
import useWindowDimensions from '../../../../../../_gori/hooks/useWindowDimensions'
import AutomationRulesService from '../../../core/AutomationRulesService'
import {AutomationRule, OUTER_HEIGHT_SETTING} from '../../../../../../_gori/constants'
import {KTSVG, convertUserTimeZone} from '../../../../../../_gori/helpers'
import {useAuth} from '../../../../auth'
import {UseTopAlert} from '../../../../../../_gori/hooks/UseTopAlert'
import {EmptyTable} from '../../../../common/components/EmptyTable'
import {AutomationRulesModal} from '../../../modals/shipping/automation-rules'
import {ConfirmActionModal} from '../../../../../../_gori/components'
import {AutomationRulesDataProvider} from '../../../hooks'
import {AUTOMATION_SYNC_EXECUTION_TYPE, AUTOMATION_SYNC_RESULT_STATUS} from '../../../core/_const'
import {SyncAutomationWidget, SyncWidgetStatus} from './SyncAutomationWidget'

const SESSION_KEY_PROCESS_ID = 'sync_automation_process_id'
const POLLING_INTERVAL = 5000

type SortableRowProps = {
  rule: AutomationRule
  onEdit: (rule: AutomationRule) => void
  onDelete: (id: number) => void
  onToggleStatus: (id: number, isActive: boolean) => void
}

const SortableRow = ({rule, onEdit, onDelete, onToggleStatus}: SortableRowProps) => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: rule.id,
  })

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      backgroundColor: isDragging ? '#f5f8fa' : 'transparent',
    }),
    [transform, transition, isDragging]
  )

  const actionItems = [
    {
      label: intl.formatMessage({id: 'EDIT'}),
      action: () => onEdit(rule),
      className: 'cursor-pointer',
    },
    {
      label: intl.formatMessage({id: 'DEACTIVATE'}),
      action: () => onToggleStatus(rule.id, false),
      className: 'cursor-pointer',
    },
    {
      label: intl.formatMessage({id: 'DELETE'}),
      action: () => onDelete(rule.id),
      className: 'cursor-pointer',
    },
  ]

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={clsx('bg-hover-light-primary', {'opacity-50': isDragging})}
    >
      <td className='ps-4 text-center'>
        <div {...attributes} {...listeners} className='cursor-move btn btn-sm btn-icon'>
          <KTSVG path='/media/gori/settings/reorder.svg' className='svg-icon-3' />
        </div>
      </td>
      <td>
        <div className='d-flex flex-column'>
          <span className='text-gray-800 fs-6 fw-bold mb-1'>{rule.name}</span>
        </div>
      </td>
      <td className='text-muted fs-6 text-break-word'>
        <span>{rule.description}</span>
      </td>
      <td className='text-muted fs-6'>
        {convertUserTimeZone(rule.updated_at, currentUser, process.env.REACT_APP_DATE_FORMAT)}
      </td>
      <td className='text-end pe-4'>
        <DropdownButton list={actionItems} />
      </td>
    </tr>
  )
}

export const AutomationRulesPage = () => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const [searchParams] = useSearchParams()
  const [allRules, setAllRules] = useState<AutomationRule[]>([])
  const [loading, setLoading] = useState(true)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showSyncConfirm, setShowSyncConfirm] = useState(false)
  const [deleteRuleId, setDeleteRuleId] = useState<number | null>(null)
  const [showRuleModal, setShowRuleModal] = useState(false)
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncWidgetStatus, setSyncWidgetStatus] = useState<SyncWidgetStatus | null>(null)
  const [appliedRules, setAppliedRules] = useState<number[]>([])
  const [totalRules, setTotalRules] = useState<number>(0)
  const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const cancelSourceRef = useRef<any>(null)
  const {topAlertHeight} = UseTopAlert()
  const {width} = useWindowDimensions()

  const sensors = useSensors(
    useSensor(TouchSensor, {activationConstraint: {delay: 200, tolerance: 5}}),
    useSensor(PointerSensor, {activationConstraint: {distance: 5}})
  )

  const activeTab = searchParams.get('status') || 'active'

  const rules = useMemo(
    () => allRules.filter((r) => (activeTab === 'active' ? r.is_active : !r.is_active)),
    [allRules, activeTab]
  )
  const activeCount = useMemo(() => allRules.filter((r) => r.is_active).length, [allRules])
  const inactiveCount = useMemo(() => allRules.filter((r) => !r.is_active).length, [allRules])

  const fetchRules = useCallback(async () => {
    setLoading(true)
    try {
      const response = await AutomationRulesService.listAutomationRules()
      setAllRules(response.automation_rules || [])
    } catch (error) {
      console.error('Error fetching automation rules:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRules()
  }, [fetchRules])

  const handleDragEnd = async (event: DragEndEvent) => {
    const {active, over} = event

    if (!over || active.id === over.id) return

    const oldIndex = rules.findIndex((r) => r.id === active.id)
    const newIndex = rules.findIndex((r) => r.id === over.id)

    const newOrder = arrayMove(rules, oldIndex, newIndex)
    setAllRules((prev) => {
      const inactives = prev.filter((r) => !r.is_active)
      return [...newOrder, ...inactives]
    })

    debouncedReorder(newOrder)
  }

  const debouncedReorder = useRef(
    debounce(async (orderedRules: AutomationRule[]) => {
      try {
        const rule_ids = orderedRules.map((r) => r.id)
        await AutomationRulesService.reorderAutomationRules({rule_ids})
        toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
      } catch (error) {
        toast.error(intl.formatMessage({id: 'ERROR_OCCURRED'}))
        fetchRules()
      }
    }, 1000)
  ).current

  const handleEdit = (rule: AutomationRule) => {
    setEditingRule(rule)
    setShowRuleModal(true)
  }

  const handleCreateNew = () => {
    setEditingRule(null)
    setShowRuleModal(true)
  }

  const handleCloseModal = () => {
    setShowRuleModal(false)
    setEditingRule(null)
  }

  const handleDelete = (id: number) => {
    setDeleteRuleId(id)
    setShowDeleteConfirm(true)
  }

  const handleToggleStatus = async (id: number, isActive: boolean) => {
    try {
      await AutomationRulesService.updateAutomationRuleStatus(id, {is_active: isActive})
      toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
      fetchRules()
    } catch (error) {
      toast.error(intl.formatMessage({id: 'ERROR_OCCURRED'}))
    }
  }

  const handleCloseDeleteConfirmation = () => {
    setShowDeleteConfirm(false)
    setDeleteRuleId(null)
  }

  const confirmDelete = async () => {
    if (!deleteRuleId) return

    try {
      await AutomationRulesService.deleteAutomationRule(deleteRuleId)
      toast.success(intl.formatMessage({id: 'DELETE_SUCCESSFULLY'}))
      fetchRules()
    } catch (error) {
      toast.error(intl.formatMessage({id: 'ERROR_OCCURRED'}))
    } finally {
      setShowDeleteConfirm(false)
      setDeleteRuleId(null)
    }
  }

  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current)
      pollingTimerRef.current = null
    }
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel()
      cancelSourceRef.current = null
    }
  }, [])

  const startPolling = useCallback(
    (processId: string) => {
      stopPolling()
      setIsSyncing(true)
      setSyncWidgetStatus('polling')

      const poll = async () => {
        try {
          const source = axios.CancelToken.source()
          cancelSourceRef.current = source
          const result = await AutomationRulesService.getSyncResult(processId, {
            cancelToken: source.token,
          })

          const currentApplied = result.applied_rules || []
          const currentTotal = result.total_rules || 0

          // Don't update progress if applied == total but status is still processing
          if (
            result.status === AUTOMATION_SYNC_RESULT_STATUS.PROCESSING &&
            currentApplied.length === currentTotal &&
            currentTotal > 0
          ) {
            // Keep previous values, wait for next poll
          } else {
            setAppliedRules(currentApplied)
            setTotalRules(currentTotal)
          }

          if (result.status === AUTOMATION_SYNC_RESULT_STATUS.COMPLETED) {
            stopPolling()
            setSyncWidgetStatus('completed')
            sessionStorage.removeItem(SESSION_KEY_PROCESS_ID)
            setIsSyncing(false)
          } else if (result.status === AUTOMATION_SYNC_RESULT_STATUS.SKIPPED) {
            stopPolling()
            setSyncWidgetStatus('skipped')
            sessionStorage.removeItem(SESSION_KEY_PROCESS_ID)
            setIsSyncing(false)
          }
        } catch (error: any) {
          if (axios.isCancel(error)) return
          console.error('Error polling sync result:', error)
          stopPolling()
          setSyncWidgetStatus('error')
          sessionStorage.removeItem(SESSION_KEY_PROCESS_ID)
          setIsSyncing(false)
        }
      }

      poll()
      pollingTimerRef.current = setInterval(poll, POLLING_INTERVAL)
    },
    [stopPolling]
  )

  const handleCloseWidget = useCallback(() => {
    setSyncWidgetStatus(null)
    setAppliedRules([])
    setTotalRules(0)
  }, [])

  const handleSyncAutomation = async () => {
    setIsSyncing(true)
    try {
      const response = await AutomationRulesService.syncAutomationRules()

      if (response.execution_type === AUTOMATION_SYNC_EXECUTION_TYPE.DIRECT) {
        const {applied_rules, total_rules} = response
        toast.success(
          intl.formatMessage(
            {id: 'SYNC_AUTOMATION_SUCCESS'},
            {applied: applied_rules?.length || 0, total: total_rules}
          )
        )
        setIsSyncing(false)
      } else if (response.execution_type === AUTOMATION_SYNC_EXECUTION_TYPE.QUEUED) {
        if (response.process_id) {
          sessionStorage.setItem(SESSION_KEY_PROCESS_ID, response.process_id)
          setTotalRules(response.total_rules || 0)
          startPolling(response.process_id)
        } else {
          toast.error(intl.formatMessage({id: 'SYNC_ALREADY_RUNNING'}))
          setIsSyncing(false)
        }
      }
    } catch (error) {
      toast.error(intl.formatMessage({id: 'ERROR_OCCURRED'}))
      setIsSyncing(false)
    }
  }

  useEffect(() => {
    const savedProcessId = sessionStorage.getItem(SESSION_KEY_PROCESS_ID)
    if (savedProcessId) {
      startPolling(savedProcessId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      stopPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tabsData = useMemo(
    () => [
      {label: 'Active', value: 'active', count: activeCount},
      {label: 'Inactive', value: 'inactive', count: inactiveCount},
    ],
    [activeCount, inactiveCount]
  )

  return (
    <AutomationRulesDataProvider>
      <div className='d-flex justify-content-start w-100 settings-header table-settings-header'>
        <h1 className='d-flex flex-column text-dark fw-boldest my-0 fs-1'>
          {intl.formatMessage({id: 'AUTOMATION_RULES'})}
        </h1>
      </div>
      <p className='fw-bold setting-subtitle mb-4 fs-6'>
        {intl.formatMessage({id: 'AUTOMATION_RULES_DESC'})}
      </p>
      <TableTabs
        keyCheckActive='status'
        dataTabs={tabsData}
        disableTabAll
        classNameTabs='shipping-address-tabs'
        classNameChild='mt-0'
        childrenTab={
          <div
            className={clsx('d-flex flex-row mb-3 gap-2 justify-content-between', {
              'w-100 mb-5': width <= 500,
            })}
          >
            <Button
              className='btn btn-advanced-primary me-3 w-100'
              classNameDiv='col-5'
              label={intl.formatMessage({id: 'NEW_RULE'})}
              icon={
                <KTSVG
                  path='/media/gori/orders/plus.svg'
                  className='svg-icon-2 icon-advanced-primary'
                />
              }
              event={handleCreateNew}
            />
            <Button
              className='btn btn-advanced-secondary w-100'
              classNameDiv='col-7'
              event={() => setShowSyncConfirm(true)}
              disabled={isSyncing || activeCount === 0}
            >
              {isSyncing && (
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                ></span>
              )}
              {intl.formatMessage({id: 'SYNC_AUTOMATIONS'})}
            </Button>
          </div>
        }
        children={
          <TableWrapper>
            <div className='table-content--inset-inner h-100'>
              {loading ? (
                <TableSkeleton />
              ) : (
                <TableBody
                  className='px-0 automation-rules-table-body'
                  height={`calc(100vh - ${OUTER_HEIGHT_SETTING + topAlertHeight}px)`}
                >
                  {rules.length === 0 ? (
                    <EmptyTable label='YOU_HAVE_NO_AUTOMATION_RULE_TO_VIEW' />
                  ) : (
                    <table className='table align-middle table-row-dashed gy-4 dataTable no-footer automation-rules-table'>
                      <thead>
                        <tr className='text-start text-muted fw-bolder text-uppercase gs-0'>
                          <th className='ps-4 col-drag'></th>
                          <th className='col-name'>{intl.formatMessage({id: 'NAME'})}</th>
                          <th className='col-description'>
                            {intl.formatMessage({id: 'DESCRIPTION'})}
                          </th>
                          <th className='col-last-modified'>
                            {intl.formatMessage({id: 'LAST_MODIFIED_DATE'})}
                          </th>
                          <th className='text-end pe-4 col-actions'>
                            {intl.formatMessage({id: 'ACTIONS'})}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeTab === 'active' ? (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                          >
                            <SortableContext
                              items={rules.map((r) => r.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {rules.map((rule) => (
                                <SortableRow
                                  key={rule.id}
                                  rule={rule}
                                  onEdit={handleEdit}
                                  onDelete={handleDelete}
                                  onToggleStatus={handleToggleStatus}
                                />
                              ))}
                            </SortableContext>
                          </DndContext>
                        ) : (
                          rules.map((rule) => (
                            <tr key={rule.id} className='bg-hover-light-primary'>
                              <td className='ps-4'></td>
                              <td>
                                <div className='d-flex flex-column'>
                                  <span className='text-gray-800 fw-bold fs-6'>{rule.name}</span>
                                </div>
                              </td>
                              <td className='text-muted fs-6 text-break-word'>
                                {rule.description}
                              </td>
                              <td className='text-muted fs-6'>
                                {convertUserTimeZone(
                                  rule.updated_at,
                                  currentUser,
                                  process.env.REACT_APP_DATE_FORMAT
                                )}
                              </td>
                              <td className='text-end pe-4'>
                                <DropdownButton
                                  list={[
                                    {
                                      label: intl.formatMessage({id: 'EDIT'}),
                                      action: () => handleEdit(rule),
                                      className: 'cursor-pointer',
                                    },
                                    {
                                      label: intl.formatMessage({id: 'ACTIVATE'}),
                                      action: () => handleToggleStatus(rule.id, true),
                                      className: 'cursor-pointer',
                                    },
                                    {
                                      label: intl.formatMessage({id: 'DELETE'}),
                                      action: () => handleDelete(rule.id),
                                      className: 'cursor-pointer',
                                    },
                                  ]}
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </TableBody>
              )}
            </div>
          </TableWrapper>
        }
      />

      <AutomationRulesModal
        show={showRuleModal}
        handleClose={handleCloseModal}
        reloadTable={fetchRules}
        editingRule={editingRule}
      />

      <ConfirmActionModal
        show={showDeleteConfirm}
        classNameDialog='mw-850px'
        title={intl.formatMessage({id: 'ARE_YOU_SURE'})}
        message={
          <span className='fw-bold text-center'>
            <p>{intl.formatMessage({id: 'ARE_YOU_SURE_YOU_WANT_TO_PROCEED'})}</p>
            <p>
              {intl.formatMessage({
                id: 'THIS_ACTION_CANNOT_BE_UNDONE_PLEASE_REVIEW_YOUR_DECISION_CAREFULLY_BEFORE_PROCEEDING',
              })}
            </p>
          </span>
        }
        messagePrimary={intl.formatMessage({id: 'SUBMIT'})}
        messageSecondary={intl.formatMessage({id: 'CANCEL'})}
        handlePrimaryAction={confirmDelete}
        handleSecondaryAction={handleCloseDeleteConfirmation}
        handleClose={handleCloseDeleteConfirmation}
      />

      <ConfirmActionModal
        show={showSyncConfirm}
        classNameDialog='mw-850px'
        title={intl.formatMessage({id: 'ARE_YOU_SURE'})}
        message={
          <span className='fw-bold text-center'>
            <p>{intl.formatMessage({id: 'ARE_YOU_SURE_YOU_WANT_TO_PROCEED'})}</p>
            <p>{intl.formatMessage({id: 'SYNC_AUTOMATION_CONFIRM_MESSAGE'})}</p>
          </span>
        }
        messagePrimary={intl.formatMessage({id: 'YES'})}
        messageSecondary={intl.formatMessage({id: 'CANCEL'})}
        handlePrimaryAction={() => {
          setShowSyncConfirm(false)
          handleSyncAutomation()
        }}
        handleSecondaryAction={() => setShowSyncConfirm(false)}
        handleClose={() => setShowSyncConfirm(false)}
      />

      {syncWidgetStatus && (
        <SyncAutomationWidget
          status={syncWidgetStatus}
          appliedRules={appliedRules}
          totalRules={totalRules}
          onClose={handleCloseWidget}
        />
      )}
    </AutomationRulesDataProvider>
  )
}
